/*
 * <gift-card-addon>
 * Adds a gift card product (with a personalised message as a line item
 * property) together with the current page product to the cart, then redirects
 * to the cart page.
 */
if (!customElements.get('gift-card-addon')) {
  class GiftCardAddon extends HTMLElement {
    constructor() {
      super();

      this.button = this.querySelector('[data-js-gift-add]');
      this.textarea = this.querySelector('[data-js-gift-message]');
      this.counter = this.querySelector('[data-js-gift-counter]');
      this.errorEl = this.querySelector('[data-js-gift-error]');

      this.giftVariantId = this.dataset.giftVariantId;
      this.productFormId = this.dataset.productFormId;
      this.fallbackVariantId = this.dataset.productVariantId;
      this.propertyName = this.dataset.propertyName || 'Gift message';
      this.cartAddUrl = this.dataset.cartAddUrl || '/cart/add';
      this.cartUrl = this.dataset.cartUrl || '/cart';
      this.cartUpdateUrl = `${this.cartUrl}/update`;

      // Bundle summary (product page only).
      this.bundleTotalEl = this.querySelector('[data-js-bundle-total]');
      this.bundleOriginalEl = this.querySelector('[data-js-bundle-original]');
      this.giftPrice = parseInt(this.dataset.giftPrice, 10) || 0;
      this.bundleSavings = parseInt(this.dataset.bundleSavings, 10) || 0;
      this.pageVariantPrices = this.parsePageVariantPrices();
    }

    parsePageVariantPrices() {
      if (!this.dataset.pageVariantPrices) return null;
      try {
        return JSON.parse(`{${this.dataset.pageVariantPrices}}`);
      } catch (error) {
        return null;
      }
    }

    connectedCallback() {
      if (this.button) {
        this.button.addEventListener('click', this.onAdd.bind(this));
      }
      if (this.textarea && this.counter) {
        this.textarea.addEventListener('input', this.updateCounter.bind(this));
        this.updateCounter();
      }
      // Keep the bundle total in sync when the page variant changes.
      // VARIANT_CHANGE is dispatched on <product-variants> and does not bubble,
      // so listen during the capture phase from the document.
      if (this.bundleTotalEl && this.pageVariantPrices) {
        this.onVariantChange = this.updateBundleTotal.bind(this);
        document.addEventListener('VARIANT_CHANGE', this.onVariantChange, true);
      }
    }

    disconnectedCallback() {
      if (this.onVariantChange) {
        document.removeEventListener('VARIANT_CHANGE', this.onVariantChange, true);
      }
    }

    /* Recompute the discounted bundle total from the currently selected page
       variant. The strikethrough original is only shown when there is a saving,
       so it may be absent. */
    updateBundleTotal() {
      const variantId = this.getProductVariantId();
      const price = variantId != null ? this.pageVariantPrices[String(variantId)] : null;
      if (price == null) return;

      const original = price + this.giftPrice;
      const total = Math.max(original - this.bundleSavings, 0);

      this.bundleTotalEl.textContent = this.formatMoney(total);
      if (this.bundleOriginalEl) {
        this.bundleOriginalEl.textContent = this.formatMoney(original);
      }
    }

    /* Format a cents value using the shop's money format. Falls back to a plain
       dollar string if the theme's format is unavailable. */
    formatMoney(cents) {
      const format =
        (window.KROWN && KROWN.settings && KROWN.settings.shop_money_format) || '${{amount}}';
      const amount = (cents / 100).toFixed(2);
      const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const noDecimals = withCommas.replace(/\.\d+$/, '');

      return format
        .replace(/\{\{\s*amount\s*\}\}/, withCommas)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDecimals)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, noDecimals.replace(/,/g, '.'));
    }

    updateCounter() {
      const max = this.textarea.getAttribute('maxlength');
      this.counter.textContent = `${this.textarea.value.length} / ${max}`;
    }

    /* Read the variant the customer currently has selected on the page; fall
       back to the variant rendered at page load if the form input is missing. */
    getProductVariantId() {
      const form = this.productFormId ? document.getElementById(this.productFormId) : null;
      const input = form ? form.querySelector('input[name="id"]') : null;
      return input && input.value ? input.value : this.fallbackVariantId;
    }

    setLoading(state) {
      if (!this.button) return;
      this.button.classList.toggle('is-loading', state);
      this.button.disabled = state;
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.classList.remove('hide');
    }

    clearError() {
      if (this.errorEl) this.errorEl.classList.add('hide');
    }

    /* Remove any gift card already in the cart so only one is ever present.
       Returns a promise that resolves once the existing gift card (if any)
       has been removed. */
    removeExistingGiftCard() {
      const giftId = Number(this.giftVariantId);

      return fetch(`${this.cartUrl}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => {
          const hasGiftCard = cart.items.some((item) => item.variant_id === giftId);
          if (!hasGiftCard) return null;

          // Setting the variant quantity to 0 removes every line for that
          // variant, regardless of the message property attached.
          return fetch(`${this.cartUpdateUrl}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({ updates: { [giftId]: 0 } })
          });
        });
    }

    onAdd() {
      this.clearError();

      if (!this.giftVariantId) {
        this.showError('This gift card is currently unavailable.');
        return;
      }

      const items = [];

      // The page product (current selected variant).
      const productVariantId = this.getProductVariantId();
      if (productVariantId) {
        items.push({ id: productVariantId, quantity: 1 });
      }

      // The gift card, with the message attached as a line item property.
      const giftItem = { id: this.giftVariantId, quantity: 1 };
      const message = this.textarea ? this.textarea.value.trim() : '';
      if (message) {
        giftItem.properties = {};
        giftItem.properties[this.propertyName] = message;
      }
      items.push(giftItem);

      this.setLoading(true);

      // Drop any existing gift card first so the new one replaces it, then add.
      this.removeExistingGiftCard()
        .then(() =>
          fetch(`${this.cartAddUrl}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({ items })
          })
        )
        .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
        .then(({ ok, data }) => {
          if (!ok) {
            this.setLoading(false);
            this.showError((data && data.description) || 'Sorry, this could not be added to your cart.');
            return;
          }
          window.location.href = this.cartUrl;
        })
        .catch(() => {
          this.setLoading(false);
          this.showError('Something went wrong. Please try again.');
        });
    }
  }

  customElements.define('gift-card-addon', GiftCardAddon);
}
